import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useHistory, useLocation, useParams } from 'react-router-dom'
import {
  IconArrowUp,
  IconArrowDown,
  IconChartBar,
  IconCheck,
  IconFileText,
  IconLock,
  IconPencil,
  IconSparkles,
  IconX,
} from '@tabler/icons-react'
import classNames from 'classnames'
import { withLDConsumer } from 'launchdarkly-react-client-sdk'
import { isEmpty, isNil, findLast, findLastIndex } from 'lodash'
import queryString from 'query-string'

import { Button } from '@/components/buttons/Button'
import { Loader } from '@/components/loaders/Loader'
import { TextInput } from '@/components/forms/TextInput'
import { CopyButton } from '@/components/buttons/CopyButton'
import { PoweredByAI } from '@/components/ai/PoweredByAI'
import { Pill } from '@/components/pills/Pill'
import { SupportAccessBanner } from '@/components/banners/SupportAccessBanner'
import {
  prependSession,
  reset,
  setActiveSessionTitle,
  setPendingInteractive,
} from '@/reducers/kodi/kodi.redux'
import { hasKodiEntitlement } from '@/reducers/auth/currentUser.redux'
import { REPORT_CHART_QUERY_PARAM } from '@/views/KodiReports/kodiReports.constants'
import { getReportChartData } from './api/kodiReportsApi'
import { useKodiWebSocket } from './hooks/useKodiWebSocket'
import { useKodiSessionMutations } from './hooks/useKodiSessionMutations'
import { KodiMarkIcon } from './KodiMarkIcon'
import { KodiChartMessage } from './components/KodiChartMessage'
import { KodiTableMessage } from './components/KodiTableMessage'
import { KodiBackfillProgressMessage } from './components/KodiBackfillProgressMessage'
import { KodiToolMessage } from './components/KodiToolMessage'
import { KodiArtifactMessage } from './components/KodiArtifactMessage'
import { KodiToolGroup } from './components/KodiToolGroup'
import { KodiApprovalPrompt } from './components/KodiApprovalPrompt'
import { KodiInteractivePrompt } from './components/KodiInteractivePrompt'
import { KodiMarkdown } from './kodiMarkdown'
import { KodiFeedbackButton } from './components/KodiFeedbackButton'
import { KodiAuditButton } from './components/audit/KodiAuditButton'
import { KodiSidebar } from './components/KodiSidebar'
import { KodiShareButton } from './components/KodiShareButton'
import { KodiUsageStatus } from './components/KodiUsageStatus'
import { KodiTurnLoader } from './components/KodiTurnLoader'
import {
  ARTIFACT_TYPE_CHART,
  ARTIFACT_TYPE_TABLE,
  ARTIFACT_TYPE_BACKFILL,
  MESSAGE_ROLES,
  TOOL_STATUSES,
  ARTIFACT_TOOLS,
  COWORK_SUGGESTIONS,
  KODI_WALKTHROUGH,
  REPORT_CHART_SUGGESTIONS,
  CONNECTION_STATES,
  CONNECTION_STATE_LABELS,
  AT_BOTTOM_EPSILON_PX,
  NEAR_BOTTOM_PX,
  sessionDisplayName,
  TITLE_REVEAL_MS,
  UNTITLED_SESSION_LABEL,
} from './kodi.constants'

import './Kodi.scss'

const DUPLICATE_QUESTION =
  "This isn't your session. Would you like to duplicate it to send your latest message?"
const DUPLICATE_OPTIONS = [
  {
    label: 'Duplicate',
    description: 'Copy this conversation into a session you own and send your message there.',
  },
  { label: 'Cancel' },
]

// Keyed by suggestion, so a pill without an entry simply renders without an icon.
const SUGGESTION_ICONS = {
  [KODI_WALKTHROUGH.key]: IconSparkles,
}

// 4001 (session not verified) is fixed by signing in again; 4003 is a real denial.
const accessDeniedMessage = (authFailed) =>
  authFailed
    ? 'Kodi could not verify your session. Sign out and sign back in to reconnect.'
    : 'You do not have access to this Kodi session.'

const KodiPageComponent = ({ flags = {} }) => {
  const dispatch = useDispatch()
  const history = useHistory()
  const location = useLocation()
  const { id } = useParams()
  const {
    messages,
    loading,
    restoring,
    readOnly,
    awaitingApproval,
    pendingInteractive,
    sessions,
    activeSessionTitle,
  } = useSelector((state) => state.kodi)
  const currentUser = useSelector((state) => state.currentUser)
  const { organizationid } = currentUser
  const { rename, fork } = useKodiSessionMutations()
  const [renaming, setRenaming] = useState(false)
  const [renameValue, setRenameValue] = useState('')
  const [input, setInput] = useState('')
  const [showScrollButton, setShowScrollButton] = useState(false)
  // Live-region text for the "response copied" announcement (option a: no toast). Kept separate
  // from the button's aria-label so the label stays stable.
  const [copyAnnouncement, setCopyAnnouncement] = useState('')
  const [sidebarOpen, setSidebarOpen] = useState(true)
  // A chart opened from Kodi Reports ({ cardId, title }), sent with the next message so
  // the backend loads it into that turn. Cleared once sent: after that it's part of the
  // conversation.
  const [attachedChart, setAttachedChart] = useState(null)
  // The message typed into a read-only session; non-null while the duplicate modal is up.
  const [duplicatePrompt, setDuplicatePrompt] = useState(null)
  const [duplicating, setDuplicating] = useState(false)
  // The in-flight duplicate request; dismissing the modal drops it so a late
  // response can't navigate or send on the user's behalf.
  const duplicateRequestRef = useRef(null)
  // Held across the hop into the duplicate, sent once that session is connected.
  const pendingPromptRef = useRef(null)
  // Route id as of the last render, undefined once the page unmounts: a fork
  // still pending must not pull the user back into Kodi or another session.
  const routeIdRef = useRef(id)
  routeIdRef.current = id
  useEffect(
    () => () => {
      routeIdRef.current = undefined
    },
    []
  )
  const messagesEndRef = useRef(null)
  const messagesContainerRef = useRef(null)
  const inputRef = useRef(null)
  // Whether streaming updates should keep pinning the view to the bottom. Starts
  // true (a fresh chat is at the bottom) and flips off the moment the user
  // scrolls up to read back, so an in-flight response no longer yanks them down.
  // Re-armed on send (see sendAndFollow) so the user's own message jumps into view.
  const stickToBottomRef = useRef(true)
  // Previous scrollTop, so handleScroll can tell which *direction* the container moved.
  // Direction is what makes the flag above trustworthy: our own pinning only ever moves
  // down, so upward movement is unambiguously the user and needs no tolerance window.
  const lastScrollTopRef = useRef(0)

  const {
    connectionState,
    isStreaming,
    isStopping,
    streamingText,
    serverSessionId,
    ownerOrgId,
    isUnredactedAccess,
    accessDenied,
    authFailed,
    hostTurnStartIndex,
    sendMessage,
    sendEvent,
    stopStreaming,
    resetChat,
  } = useKodiWebSocket(id)

  const [confirmedSessionId, setConfirmedSessionId] = useState(null)
  useEffect(() => {
    if (serverSessionId) {
      setConfirmedSessionId(serverSessionId)
    }
  }, [serverSessionId])

  const isConnected = connectionState === CONNECTION_STATES.CONNECTED
  const hasMessages = !isEmpty(messages)
  const activeSession = sessions.find((session) => session.session_id === id)
  // Prefer the connection-scoped title (set by auth_success / select — the only
  // source for shared sessions absent from the drawer), but fall back to the
  // drawer row so an owner's own session never shows a blank header on a direct
  // URL load or when the backend sends no name.
  const headerTitle = activeSessionTitle || (activeSession ? sessionDisplayName(activeSession) : '')
  // A route id with no messages means a restore is in flight — until auth_success confirms
  // a *different* id, the BE's way of saying that chat couldn't be opened (deleted, stale
  // link) and it minted a fresh one. Nothing left to restore, so stop spinning.
  const awaitingRestore =
    Boolean(id) && !hasMessages && (!serverSessionId || serverSessionId === id)
  // Mirrors the fork endpoint's gates: Kodi entitlement, same org as the owner (a
  // parent-org viewer admitted through the hierarchy gets a 403), and not org 1.
  // Viewers who fail them see no composer rather than a modal that can't succeed.
  const canDuplicate =
    organizationid !== 1 &&
    hasKodiEntitlement(currentUser) &&
    Number(ownerOrgId) === Number(organizationid)
  const submitDisabled = !input.trim() || loading || restoring || !isConnected
  const submitDisabledTooltip = !isConnected
    ? 'Kodi is not connected'
    : restoring
      ? 'Restoring the conversation'
      : loading
        ? 'Kodi is still responding'
        : 'Type a message to send'
  // Same gating as the composer minus the empty-input check: a reply can't be sent
  // while a turn is in flight, restoring, or disconnected (sendMessage would no-op).
  const approvalDisabled = loading || restoring || !isConnected

  // The approval actions attach to the proposed change — the last assistant message.
  const lastKodiIndex = findLastIndex(messages, (message) => message.role === MESSAGE_ROLES.KODI)
  const pendingApprovalId =
    awaitingApproval && !readOnly && lastKodiIndex !== -1 ? messages[lastKodiIndex].id : null

  // Keep the input focused so the user can keep typing without re-clicking.
  // The textarea is rendered in two branches (empty-state vs chat), so the
  // first message remounts it; a response finishing also flips `loading`.
  // Restore focus on both transitions.
  useEffect(() => {
    // Don't grab focus while the interactive window is open — it owns keyboard
    // focus (number-key selection), and child effects run before this parent one,
    // so without the guard the composer would steal focus right after the panel mounts.
    if (!restoring && isConnected && !readOnly && !pendingInteractive) {
      inputRef.current?.focus({ preventScroll: true })
    }
  }, [loading, restoring, isConnected, hasMessages, readOnly, pendingInteractive])
  // A viewer's `loading` flips with the owner's turns, so not on that: focus their
  // composer when it appears and when the duplicate window hands it back.
  useEffect(() => {
    if (readOnly && !restoring && isConnected && duplicatePrompt === null) {
      inputRef.current?.focus({ preventScroll: true })
    }
  }, [readOnly, restoring, isConnected, hasMessages, duplicatePrompt])

  // Sync the URL to the server-confirmed session id once the user has
  // actually engaged. Gating on a user message specifically (not just any
  // message) keeps reconnect-time error bubbles from counting: when the BE
  // is degraded it sends ``Service temporarily unavailable`` on every
  // failed connection, the BE mints a fresh session_id per attempt, and
  // each cycle would otherwise replace the URL + prepend a new synthetic
  // sidebar row — spawning a fake "conversation" per reconnect for a
  // session the user never typed into. Restore replays the saved USER
  // messages, so a successfully restored session still satisfies this gate.
  const hasUserActivity = messages.some((m) => m.role === MESSAGE_ROLES.USER)
  useEffect(() => {
    if (hasUserActivity && serverSessionId && id !== serverSessionId) {
      history.replace(`/kodi/${serverSessionId}`)
      dispatch(
        prependSession({
          id: serverSessionId,
          session_id: serverSessionId,
          name: null,
          created_at: new Date().toISOString(),
          synthetic: true,
        })
      )
    }
  }, [hasUserActivity, serverSessionId, id, history, dispatch])

  const scrollToBottom = () => {
    // Explicit jump-to-bottom (scroll button / approval expand) also resumes
    // follow-along so subsequent streaming updates keep the view pinned.
    stickToBottomRef.current = true
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  // Follow-along pinning. Instant and container-scoped on purpose: this runs on every
  // token, and a `smooth` scrollIntoView here would keep the container permanently
  // mid-animation, whose trailing scroll events used to overwrite the user's scroll-up
  // and yank them back down. Writing scrollTop also can't move any ancestor or the page.
  const pinToBottom = () => {
    const container = messagesContainerRef.current
    if (!container) {
      return
    }
    container.scrollTop = container.scrollHeight
    // Record it as our own position so the resulting scroll event isn't read as movement.
    lastScrollTopRef.current = container.scrollTop
  }

  // Swapping conversations starts a fresh chat at the bottom. Clearing the remembered
  // position matters: the outgoing session's scrollTop would otherwise make the new
  // session's first scroll event look like an upward scroll and kill follow-along.
  const resetFollowAlong = () => {
    stickToBottomRef.current = true
    lastScrollTopRef.current = 0
  }

  const handleScroll = () => {
    const container = messagesContainerRef.current
    if (!container) {
      return
    }
    const scrolledUp = container.scrollTop < lastScrollTopRef.current
    lastScrollTopRef.current = container.scrollTop
    const distanceFromBottom = container.scrollHeight - container.scrollTop - container.clientHeight

    if (distanceFromBottom <= AT_BOTTOM_EPSILON_PX) {
      // Sitting at the true bottom — follow along. Ordered before the direction check so a
      // shrink that *lands* at the bottom isn't misread: content shrinking (the streaming
      // bubble giving way to the final message) clamps scrollTop downward, which otherwise
      // looks like an upward scroll even though the user never moved. A shrink that leaves
      // them further up still reads as a scroll-up, but by then they'd scrolled away and
      // following is already off, so the misread has nothing to change.
      stickToBottomRef.current = true
    } else if (scrolledUp) {
      // Any upward scroll away from the bottom means "let me read" — stop following
      // immediately, no matter how small. pinToBottom only ever scrolls down, and the
      // branch above absorbs its landing position, so our own pinning stays out of here.
      stickToBottomRef.current = false
    }

    setShowScrollButton(distanceFromBottom > NEAR_BOTTOM_PX)
  }

  useEffect(() => {
    if (isEmpty(flags)) {
      return
    }
    // Org 1 can view a shared session (id in the URL) but can't start their
    // own, so redirect them only when there's no session to view.
    if (!flags?.baltoCoworkEnabled || (organizationid === 1 && !id)) {
      history.push('/')
      return
    }
    // Org 1 holds no entitlement of its own and is already handled above.
    //
    // `!id` mirrors the org-1 carve-out: losing Kodi costs you the ability to
    // START a session, not to open one shared with you. The BE serves such a
    // viewer read-only rather than refusing them, so bouncing them here would
    // make that unreachable and silently break session sharing for anyone
    // whose org has Kodi switched off.
    //
    // No longer waits on currentOrganization: the entitlement now comes
    // pre-resolved on currentUser, which is hydrated from localStorage before
    // first render, and is absent-means-allow until core-service answers.
    if (organizationid !== 1 && !id && !hasKodiEntitlement(currentUser)) {
      history.push('/')
    }
  }, [flags, organizationid, id, currentUser, history])

  const reportChartId = queryString.parse(location.search)[REPORT_CHART_QUERY_PARAM]

  // Only a fresh chat takes a chart: "Ask Kodi" on Kodi Reports always lands on bare
  // /kodi. It attaches straight from the URL, so a message sent before its title
  // loads still carries it; the load only fills in the title. Each update checks the
  // chart is still the attached one, so a load that lands after the message went out
  // can't attach it to the next message. A chart that can't be loaded (removed since)
  // drops off; the backend also tells Kodi when an attached chart is gone.
  useEffect(() => {
    if (id || isEmpty(reportChartId)) {
      setAttachedChart(null)
      return undefined
    }
    setAttachedChart({ cardId: reportChartId, title: null })
    const stillAttached = (current) => current?.cardId === reportChartId
    let cancelled = false
    getReportChartData(dispatch, reportChartId)
      .then((response) => {
        if (cancelled) {
          return
        }
        const title = response.title || response.chart?.title || 'Pinned chart'
        setAttachedChart((current) => (stillAttached(current) ? { ...current, title } : current))
      })
      .catch(() => {
        if (!cancelled) {
          setAttachedChart((current) => (stillAttached(current) ? null : current))
        }
      })
    return () => {
      cancelled = true
    }
  }, [id, reportChartId, dispatch])

  // Keyed on `messages` identity, not length: in-place updates (tool progress, tool
  // results) grow the rendered height, so they have to re-pin too. The rAF lets the
  // appended content paint before we measure it.
  useEffect(() => {
    if (restoring || !stickToBottomRef.current) {
      return
    }
    requestAnimationFrame(() => {
      if (stickToBottomRef.current) {
        pinToBottom()
      }
    })
  }, [messages, restoring])

  useEffect(() => {
    if (restoring || !stickToBottomRef.current) {
      return
    }
    // Not scrollToBottom(): that helper re-arms follow-along, which is right for an
    // explicit click but would make the streaming path assert the very flag it's meant
    // to be obeying.
    pinToBottom()
  }, [streamingText, restoring])

  // Typing grows the composer, which takes that height out of the message list above it.
  // Without this the bottom of the transcript would slide out of view as the box expands.
  // Layout effect so the re-pin lands in the same frame as the resize (no visible jump),
  // and it obeys follow-along like the streaming path: someone who has scrolled up to
  // read while composing stays where they are.
  useLayoutEffect(() => {
    if (restoring || !stickToBottomRef.current) {
      return
    }
    pinToBottom()
  }, [input, restoring])

  // A restored session should open at the bottom, so this one ignores follow-along.
  useEffect(() => {
    if (!restoring && hasMessages) {
      pinToBottom()
    }
  }, [restoring])

  // Every send goes through here so they all re-arm follow-along: the user's own message
  // (and the reply it triggers) should pull the view back to the bottom even if they'd
  // scrolled up. Covers the composer, "Generate report", and the approval/interactive
  // replies — the last three used to send without re-arming.
  const sendAndFollow = (content, options) => {
    stickToBottomRef.current = true
    // Only approve/decline pass options; keep the ordinary path a single-arg call.
    if (options) {
      sendMessage(content, options)
    } else {
      sendMessage(content)
    }
  }

  const sendPrompt = (content) => {
    if (attachedChart) {
      sendAndFollow(content, { reportChartId: attachedChart.cardId })
      setAttachedChart(null)
      return
    }
    sendAndFollow(content)
  }

  const handleSubmit = () => {
    if (submitDisabled) {
      return
    }
    if (readOnly) {
      setDuplicatePrompt(input.trim())
      return
    }
    sendPrompt(input.trim())
    setInput('')
  }

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault()
      handleSubmit()
    }
  }

  const handleNewChat = () => {
    // Bail only on a fresh, unused chat at bare /kodi. A route id still needs clearing with
    // no messages: a chat that can't be opened (deleted, stale link) would otherwise strand
    // the user on it — same for onActiveSessionDeleted before the session had any messages.
    if (!hasMessages && !id) {
      return
    }

    pendingPromptRef.current = null
    dispatch(reset())
    dispatch(setActiveSessionTitle(null))
    resetChat()
    resetFollowAlong()
    history.push('/kodi')
  }

  const handleSelectSession = (sessionId, sessionName) => {
    // Re-clicking the already-active session would otherwise tear down the
    // WS and remount without a session_id (resetChat clears sessionId.current
    // and the route useEffect won't re-run because the URL didn't change),
    // leaving the page stuck on the "Restoring conversation…" spinner.
    if (sessionId === id) {
      return
    }
    // Leaving a duplicate before it connected: its message must not fire on a later visit.
    pendingPromptRef.current = null
    dispatch(reset())
    // Optimistic title so the header updates instantly; auth_success confirms it.
    dispatch(setActiveSessionTitle(sessionName))
    resetChat()
    resetFollowAlong()
    history.push(`/kodi/${sessionId}`)
  }

  // Confirmed from the read-only window: copy the session through the latest
  // answer on screen, move into the copy, and send the typed message there once
  // it's connected.
  const handleDuplicate = async () => {
    const request = { sourceId: id, content: duplicatePrompt }
    duplicateRequestRef.current = request
    setDuplicating(true)
    // Cut at the latest answer the viewer is looking at, not whatever the owner
    // may have finished since; the server only falls back to its own latest.
    const viewedTurn = findLast(
      messages,
      (message) => message.role === MESSAGE_ROLES.KODI && message.auditUuid
    )
    const created = await fork(request.sourceId, viewedTurn?.auditUuid)
    if (routeIdRef.current === undefined) {
      return
    }
    // Dismissed or moved on while the POST was in flight: the copy is already in
    // their sidebar, so leave them where they are. Only the request that still
    // owns the window may unlock it; a stale one must not unlock a newer request.
    if (duplicateRequestRef.current !== request) {
      return
    }
    setDuplicating(false)
    if (!created || routeIdRef.current !== request.sourceId) {
      return
    }
    setDuplicatePrompt(null)
    setInput('')
    handleSelectSession(created.session_id, created.name)
    pendingPromptRef.current = { sessionId: created.session_id, content: request.content }
  }
  const handleDuplicateClose = () => {
    duplicateRequestRef.current = null
    setDuplicating(false)
    setDuplicatePrompt(null)
  }
  // The page stays mounted across session switches (sidebar, New chat, back/forward),
  // so a window opened for one session must not follow the user into the next.
  useEffect(() => {
    duplicateRequestRef.current = null
    setDuplicating(false)
    setDuplicatePrompt(null)
    // Back/Forward never goes through handleSelectSession: a message held for a
    // copy the user left must not fire when they wander back into it later.
    // The hop into the copy itself survives, since handleDuplicate sets the ref
    // for the new id right after pushing the route.
    if (pendingPromptRef.current?.sessionId !== id) {
      pendingPromptRef.current = null
    }
  }, [id])
  useEffect(() => {
    const pending = pendingPromptRef.current
    if (
      pending &&
      pending.sessionId === id &&
      confirmedSessionId === id &&
      isConnected &&
      !awaitingRestore &&
      !restoring &&
      !loading &&
      !readOnly
    ) {
      pendingPromptRef.current = null
      sendAndFollow(pending.content)
    }
  })

  const handleGenerateReport = () => {
    sendAndFollow(
      'Generate a report summarizing our conversation and key findings, including any applicable charts.'
    )
  }

  // Clicking a suggestion starts the chat with it. Sending inline is safe here because
  // the pills only render on an empty chat, whose socket is already up — there is no
  // teardown to queue across. If it isn't up yet, fall back to filling the composer:
  // `sendMessage` no-ops on a closed socket, and a click that does nothing at all is
  // worse than one the user has to fire themselves.
  const handleSelectSuggestion = (suggestion) => {
    if (isConnected) {
      sendPrompt(suggestion)
      return
    }
    setInput(suggestion)
    inputRef.current?.focus({ preventScroll: true })
  }

  const [namingTitle, setNamingTitle] = useState(false)
  const previousHeaderTitle = useRef(headerTitle)

  useEffect(() => {
    const cameFromPlaceholder = previousHeaderTitle.current === UNTITLED_SESSION_LABEL
    previousHeaderTitle.current = headerTitle
    if (!cameFromPlaceholder || !headerTitle || headerTitle === UNTITLED_SESSION_LABEL) {
      return undefined
    }
    setNamingTitle(true)
    const timer = setTimeout(() => setNamingTitle(false), TITLE_REVEAL_MS)
    return () => clearTimeout(timer)
  }, [headerTitle])

  const startRename = () => {
    setRenameValue(activeSession?.name || '')
    setRenaming(true)
  }

  const cancelRename = () => {
    setRenaming(false)
  }

  const submitRename = () => {
    const trimmed = renameValue.trim()
    setRenaming(false)
    if (!trimmed || !activeSession || trimmed === headerTitle) {
      return
    }
    // Optimistic header update; rename() is also optimistic on the drawer and
    // rolls back + rejects on a failed PATCH, so mirror that here or the header
    // would keep showing an unsaved name.
    const previousTitle = headerTitle
    dispatch(setActiveSessionTitle(trimmed))
    rename(activeSession, trimmed).catch(() => dispatch(setActiveSessionTitle(previousTitle)))
  }

  const handleRenameKeyDown = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault()
      submitRename()
    } else if (event.key === 'Escape') {
      event.preventDefault()
      cancelRename()
    }
  }

  const groupMessages = (msgs) => {
    const groups = []
    let toolBuffer = []
    const flushTools = () => {
      if (!isEmpty(toolBuffer)) {
        groups.push({ type: 'tool_group', messages: toolBuffer, id: toolBuffer[0].id })
        toolBuffer = []
      }
    }

    msgs.forEach((message) => {
      if (message.role === MESSAGE_ROLES.TOOL) {
        const isInlineTool =
          message.toolName === ARTIFACT_TOOLS.RENDER_CHART ||
          message.toolName === ARTIFACT_TOOLS.RENDER_REPORT
        if (isInlineTool) {
          flushTools()
          groups.push({ type: 'message', message })
          return
        }
        // Hide backend-flagged internal plumbing; show product steps that carry a rationale.
        if (!message.internal && !isEmpty(message.rationale)) {
          toolBuffer.push(message)
        }
        return
      }
      flushTools()
      groups.push({ type: 'message', message })
    })

    flushTools()
    return groups
  }

  // Clear-then-set on the next frame: an identical repeat ("copy" again) still changes the live
  // region's text content, so screen readers re-announce it instead of staying silent.
  const announceCopied = () => {
    setCopyAnnouncement('')
    window.requestAnimationFrame(() => setCopyAnnouncement('Response copied to clipboard'))
  }

  const renderMessage = (message) => {
    if (message.role === MESSAGE_ROLES.ARTIFACT) {
      // Inline artifacts from the query engine render as chart/table components.
      if (message.artifactType === ARTIFACT_TYPE_CHART && message.data) {
        return (
          <KodiChartMessage
            key={message.id}
            message={{
              ...message,
              result:
                typeof message.data === 'string' ? message.data : JSON.stringify(message.data),
            }}
          />
        )
      }
      if (message.artifactType === ARTIFACT_TYPE_TABLE && message.data) {
        return <KodiTableMessage key={message.id} message={message} />
      }
      if (message.artifactType === ARTIFACT_TYPE_BACKFILL && message.data) {
        return (
          <KodiBackfillProgressMessage key={message.id} message={message} onTerminal={sendEvent} />
        )
      }
      return <KodiArtifactMessage key={message.id} message={message} />
    }

    if (message.role === MESSAGE_ROLES.TOOL) {
      if (message.toolName === ARTIFACT_TOOLS.RENDER_CHART) {
        if (message.status === TOOL_STATUSES.COMPLETE && !message.isError) {
          return <KodiChartMessage key={message.id} message={message} />
        }

        return (
          <KodiToolMessage
            key={message.id}
            message={{ ...message, rationale: message.rationale || 'Generating chart' }}
          />
        )
      }

      if (message.toolName === ARTIFACT_TOOLS.RENDER_REPORT) {
        return (
          <KodiToolMessage
            key={message.id}
            message={{ ...message, rationale: message.rationale || 'Generating report' }}
          />
        )
      }

      return <KodiToolMessage key={message.id} message={message} />
    }

    return (
      <div
        key={message.id}
        className={classNames('cowork-message', {
          'cowork-message--user': message.role === MESSAGE_ROLES.USER,
          'cowork-message--kodi': message.role === MESSAGE_ROLES.KODI,
          'cowork-message--error': message.role === MESSAGE_ROLES.ERROR,
        })}
      >
        <div className="cowork-message__content">
          {message.role === MESSAGE_ROLES.KODI ? (
            <KodiMarkdown>{message.content}</KodiMarkdown>
          ) : (
            message.content
          )}
          {message.id === pendingApprovalId && (
            <KodiApprovalPrompt
              onRespond={sendAndFollow}
              disabled={approvalDisabled}
              onExpand={scrollToBottom}
            />
          )}
        </div>
        {message.role === MESSAGE_ROLES.KODI && (
          // Quiet action row under the assistant prose. CopyButton owns the clipboard write + the
          // icon→check confirmation; the span wrapper mirrors a copy click into the page-level
          // live region for screen readers (the iconOnly button gives them nothing on its own).
          <div className="cowork-message__actions" role="toolbar" aria-label="Response actions">
            {/* Wrap only the copy button so a copy click (mouse or keyboard) mirrors
                into the live region; sibling actions (feedback) don't announce. */}
            <span onClick={announceCopied}>
              <CopyButton content={message.content} iconOnly basic aria-label="Copy response" />
            </span>
            {/* Read-only viewers of a shared session replay turns (which carry
                auditUuid) but must not trigger a backend write — gate like the
                other write controls on the page. */}
            {!readOnly && message.auditUuid && (
              <KodiFeedbackButton
                auditUuid={message.auditUuid}
                feedbackType={message.feedbackType}
              />
            )}
            {/* Dev-mode audit trail: org-1 (Balto internal) only. The endpoint
                also enforces org-1, so this gate is UX, not the security boundary. */}
            {organizationid === 1 && message.auditUuid && (
              <KodiAuditButton auditUuid={message.auditUuid} />
            )}
          </div>
        )}
      </div>
    )
  }

  const renderInput = () => {
    if (duplicatePrompt !== null) {
      // Takes the composer's place until the viewer picks; the typed message is
      // kept in state and comes back with the textarea on Cancel.
      return (
        <KodiInteractivePrompt
          question={DUPLICATE_QUESTION}
          options={DUPLICATE_OPTIONS}
          allowOther={false}
          onRespond={(label) => {
            if (label === DUPLICATE_OPTIONS[0].label) {
              handleDuplicate()
            } else {
              handleDuplicateClose()
            }
          }}
          onClose={handleDuplicateClose}
          // Only Duplicate locks while the POST is in flight; Cancel stays live.
          loadingLabel={duplicating ? DUPLICATE_OPTIONS[0].label : undefined}
        />
      )
    }
    return (
      <>
        {pendingInteractive && !readOnly && (
          <KodiInteractivePrompt
            key={pendingInteractive.question}
            question={pendingInteractive.question}
            options={pendingInteractive.options}
            multiSelect={pendingInteractive.multiSelect}
            onRespond={sendAndFollow}
            onClose={() => dispatch(setPendingInteractive(null))}
            disabled={approvalDisabled}
          />
        )}
        <div
          data-testid="cowork-input-wrapper"
          className={classNames({ 'animated-gradient-border': loading })}
        >
          <div
            className="fake-textarea"
            onClick={(event) => {
              if (!event.target.closest('button, a, input, textarea, [role="button"]')) {
                inputRef.current?.focus()
              }
            }}
          >
            <div className="cowork-input-header">
              <textarea
                data-testid="cowork-input"
                placeholder={
                  attachedChart
                    ? 'Ask about this chart...'
                    : 'Ask Kodi to coach your agents, pull a report, or find anything in your calls...'
                }
                value={input}
                ref={inputRef}
                onChange={(event) => setInput(event.target.value)}
                onKeyDown={handleKeyDown}
                // Resting height only — the composer grows with its content (see the
                // `field-sizing` rule in Kodi.scss), and this is what browsers without
                // that support fall back to.
                rows={4}
              />
            </div>
            <div className="cowork-input-footer">
              {attachedChart && (
                // Same reserved left slot as Generate Report, which only shows once the
                // chat has messages — and by then the chart has been sent.
                <div className="flex-align-center small-gap">
                  <Pill
                    brand
                    icon={<IconChartBar />}
                    dataTestId="cowork-attached-chart"
                    tooltip="Kodi will answer about this chart from Kodi Reports"
                  >
                    {attachedChart.title || 'Loading chart…'}
                  </Pill>
                  <Button
                    square
                    small
                    basic
                    aria-label="Remove chart"
                    data-testid="cowork-attached-chart-remove"
                    onClick={() => setAttachedChart(null)}
                    icon={<IconX />}
                  />
                </div>
              )}
              {hasMessages && !readOnly && (
                // Lives in the composer's reserved left slot. Sends the canned report prompt.
                // Hidden in the empty/welcome state (no messages); disabled while a turn is in
                // flight or the socket is down so it can't inject a second turn.
                <Button
                  small
                  pill
                  className="cowork-generate-report"
                  icon={<IconFileText />}
                  onClick={handleGenerateReport}
                  disabled={loading || restoring || !isConnected}
                >
                  Generate Report
                </Button>
              )}
              <div className="flex-end cowork-input-actions">
                {/* A viewer can't stop the owner's turn (the BE rejects it with an
                  error frame), so they keep the disabled send button instead. */}
                {isStreaming && !readOnly ? (
                  <Button
                    critical
                    className="cowork-stop-button"
                    aria-label={isStopping ? 'Stopping streaming' : 'Stop streaming'}
                    onClick={stopStreaming}
                    // The BE ends the turn and acks with turn_complete; until then
                    // a second click would just re-send the same request.
                    disabled={isStopping}
                  >
                    {isStopping ? 'Stopping…' : 'Stop'}
                  </Button>
                ) : (
                  <Button
                    brand
                    square
                    className="cowork-submit-button"
                    data-testid="cowork-submit-button"
                    aria-label="Send message"
                    onClick={handleSubmit}
                    disabled={submitDisabled}
                    disabledTooltip={submitDisabledTooltip}
                    icon={<IconArrowUp aria-hidden="true" />}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
        <PoweredByAI className="cowork-disclaimer" />
      </>
    )
  }

  // A flat row of pills that prefill the composer rather than sending: that keeps them
  // clickable with no open socket, and leaves the user to press Enter — which, for the
  // tour, is the thing being taught.
  const renderSuggestions = () => (
    <div className="cowork-suggestions__pills" role="group" aria-label="Suggestions">
      {(attachedChart ? REPORT_CHART_SUGGESTIONS : COWORK_SUGGESTIONS).map((suggestion) => {
        const PillIcon = SUGGESTION_ICONS[suggestion.key]
        return (
          <Pill
            key={suggestion.key}
            clickable
            outlined
            icon={PillIcon ? <PillIcon size={14} /> : null}
            role="button"
            tabIndex={0}
            onClick={() => handleSelectSuggestion(suggestion.prompt)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault()
                handleSelectSuggestion(suggestion.prompt)
              }
            }}
            dataTestId={`cowork-suggestion-pill-${suggestion.key}`}
          >
            {suggestion.label}
          </Pill>
        )
      })}
    </div>
  )

  const groupedMessages = groupMessages(messages)
  const latestUserGroupIndex = groupedMessages.reduce((lastIndex, item, index) => {
    if (item.type === 'message' && item.message.role === MESSAGE_ROLES.USER) {
      return index
    }
    return lastIndex
  }, -1)
  // A turn the backend started (a backfill nudge) adds no user message, so
  // anchoring to the last one would drop the loader into the middle of the
  // transcript and fold the previous turn's tool pills into its drawer.
  const turnAnchorIndex = isNil(hostTurnStartIndex)
    ? latestUserGroupIndex
    : groupedMessages.length - 1
  // While the turn is in progress its tool steps live inside the "Thinking…" drawer
  // (not as separate "N steps" pills); once the turn finishes they render as a normal
  // collapsed tool group below.
  const isCurrentTurnTool = (index) =>
    isStreaming && isEmpty(streamingText) && index > turnAnchorIndex
  const currentTurnTools = groupedMessages
    .filter((item, index) => item.type === 'tool_group' && isCurrentTurnTool(index))
    .flatMap((group) => group.messages)
  // Loader shows the latest tool's live progress message (e.g. "Analyzing 100 calls… 40%"),
  // falling back to its rationale, or a cycling dog-themed phrase before one lands.
  const lastUserIndex = findLastIndex(messages, (message) => message.role === MESSAGE_ROLES.USER)
  const turnToolMessages = messages
    .slice(isNil(hostTurnStartIndex) ? lastUserIndex + 1 : hostTurnStartIndex)
    .filter((message) => message.role === MESSAGE_ROLES.TOOL && !message.internal)
  const latestTurnTool = isEmpty(turnToolMessages)
    ? null
    : turnToolMessages[turnToolMessages.length - 1]
  const activeRationale = isNil(latestTurnTool)
    ? ''
    : latestTurnTool.progressMessage || latestTurnTool.rationale || ''

  // Renders at the top of the current turn (just after the user's message); hidden (not
  // unmounted) while the answer streams so the CogitationDog animation never restarts.
  const turnLoader = isStreaming ? (
    <KodiTurnLoader
      steps={currentTurnTools}
      activeRationale={activeRationale}
      hidden={!isEmpty(streamingText)}
    />
  ) : null

  return (
    <div className="cowork-page" data-testid="cowork-page">
      {/* Unredacted-view countdown, shown only when the backend grants org 1
          unredacted access to this session (auth_success). Mounted inside
          cowork-page (a fixed-height flex column) as the first row so it's part
          of the 100vh budget and never scrolls out of the surface. Require
          ownerOrgId too: a null owner would let the banner fall back to the
          call-search filter org and mislabel whose data is being unmasked. */}
      {isUnredactedAccess && ownerOrgId && <SupportAccessBanner orgId={ownerOrgId} />}

      {/* Single page-level live region for response-action announcements (e.g. copy). */}
      <div className="cowork-visually-hidden" role="status" aria-live="polite">
        {copyAnnouncement}
      </div>

      <div className="cowork-body">
        {/* Org 1 can only view shared sessions, never their own — no sidebar. */}
        {organizationid !== 1 && (
          <KodiSidebar
            open={sidebarOpen}
            onToggle={() => setSidebarOpen((prior) => !prior)}
            onNewChat={handleNewChat}
            onSelectSession={handleSelectSession}
            onActiveSessionDeleted={handleNewChat}
          />
        )}
        <div
          className={classNames('cowork-content', {
            // No docked sidebar (collapsed, or org 1 which never has one) → give
            // the chat the freed horizontal room with a wider reading cap.
            'cowork-content--expanded': !sidebarOpen || organizationid === 1,
          })}
        >
          {/* Surface header shell — gives the surface a stable frame; title + rename appear only
              with an active session. */}
          <header
            className={classNames('cowork-surface-header', {
              // Collapsed drawer shows the floating FAB at top-left; clear it so the title
              // isn't hidden beneath it. (Org 1 has no sidebar/FAB, so exclude it.)
              'cowork-surface-header--fab-clearance': organizationid !== 1 && !sidebarOpen,
            })}
            data-testid="cowork-surface-header"
          >
            <div className="cowork-surface-header__title-cluster">
              {/* Display-only connection status, left of the title. Accessible name is always
                  present, hover reveals the label; heartbeat (connected only) is a liveness signal,
                  not decoration. Rendered for everyone — org 1 shared-session viewers have no
                  sidebar, so this is their only status indicator. */}
              <span
                className={classNames('kodi-status', {
                  'kodi-status--connected': connectionState === CONNECTION_STATES.CONNECTED,
                  'kodi-status--connecting': connectionState === CONNECTION_STATES.CONNECTING,
                  'kodi-status--disconnected': connectionState === CONNECTION_STATES.DISCONNECTED,
                })}
                role="status"
                aria-live="polite"
                aria-label={CONNECTION_STATE_LABELS[connectionState]}
                data-testid="kodi-connection-status"
              >
                <span className="kodi-status__dot" aria-hidden="true" />
                <span className="kodi-status__label">
                  {CONNECTION_STATE_LABELS[connectionState]}
                </span>
              </span>
              {readOnly && (
                // Read-only shared session: sending duplicates it first (see handleSubmit).
                // Same hover-to-reveal treatment as the status dot.
                <span
                  className="kodi-status kodi-status--readonly"
                  role="status"
                  aria-label="Read-only"
                  data-testid="kodi-readonly-status"
                >
                  <IconLock size={14} className="kodi-status__icon" aria-hidden="true" />
                  <span className="kodi-status__label">Read-only</span>
                </span>
              )}
              <KodiUsageStatus />
              {headerTitle &&
                (renaming ? (
                  <div className="cowork-surface-header__rename-form">
                    <TextInput
                      small
                      autoFocus
                      aria-label="Chat name"
                      className="cowork-surface-header__title-input"
                      data-testid="cowork-surface-title-input"
                      value={renameValue}
                      onChange={(event) => setRenameValue(event.target.value)}
                      onBlur={cancelRename}
                      onKeyDown={handleRenameKeyDown}
                    />
                    <Button
                      brand
                      small
                      square
                      aria-label="Save name"
                      data-testid="cowork-surface-rename-save"
                      onMouseDown={(event) => event.preventDefault()}
                      onClick={submitRename}
                      icon={<IconCheck />}
                    />
                    <Button
                      small
                      square
                      aria-label="Cancel rename"
                      data-testid="cowork-surface-rename-cancel"
                      onMouseDown={(event) => event.preventDefault()}
                      onClick={cancelRename}
                      icon={<IconX />}
                    />
                  </div>
                ) : (
                  <>
                    <span
                      className={classNames('cowork-surface-header__title', {
                        'cowork-surface-header__title--naming': namingTitle,
                      })}
                    >
                      {headerTitle}
                    </span>
                    {activeSession && (
                      <Button
                        basic
                        small
                        square
                        className="cowork-surface-header__rename"
                        aria-label="Rename chat"
                        data-testid="cowork-surface-rename"
                        onClick={startRename}
                        icon={<IconPencil stroke={1.5} />}
                      />
                    )}
                  </>
                ))}
            </div>
            {/* Gated on the last id auth_success confirmed, not the live socket, so a reconnect
                doesn't unmount Share (and close its dialog). A stale link is swapped for a fresh
                session id, and a session switch leaves the old id, so both stay hidden. */}
            {id && confirmedSessionId === id && !accessDenied && <KodiShareButton sessionId={id} />}
          </header>
          {accessDenied ? (
            <div className="cowork-empty-state">
              <div className="cowork-restoring" data-testid="cowork-access-denied">
                <span>{accessDeniedMessage(authFailed)}</span>
              </div>
            </div>
          ) : restoring || awaitingRestore ? (
            <div className="cowork-empty-state">
              <Loader centered label="Restoring conversation…" data-testid="cowork-restoring" />
            </div>
          ) : !hasMessages ? (
            <div className="cowork-empty-state">
              <div className="cowork-empty-state__content">
                <span className="kodi-hero-orb" aria-hidden="true">
                  <KodiMarkIcon size={26} />
                </span>
                <h1 className="cowork-empty-state__greeting">Hi, I am Kodi</h1>
                {!readOnly && renderInput()}
                {!readOnly && renderSuggestions()}
              </div>
            </div>
          ) : (
            <>
              <div
                className="cowork-messages"
                data-testid="cowork-messages"
                ref={messagesContainerRef}
                onScroll={handleScroll}
              >
                {groupedMessages.map((item, index) => {
                  let node
                  if (item.type === 'tool_group') {
                    // In-progress steps live in the "Thinking…" drawer; skip the pill.
                    node = isCurrentTurnTool(index) ? null : (
                      <KodiToolGroup key={item.id} messages={item.messages} isTurnRunning={false} />
                    )
                  } else {
                    node = renderMessage(item.message)
                  }
                  if (index === turnAnchorIndex && turnLoader) {
                    return (
                      <React.Fragment key={`turn-${item.id ?? item.message.id}`}>
                        {node}
                        {turnLoader}
                      </React.Fragment>
                    )
                  }
                  return node
                })}

                {isStreaming && streamingText && (
                  <div className="cowork-message cowork-message--kodi cowork-message--streaming">
                    <div className="cowork-message__content">
                      <KodiMarkdown>{streamingText}</KodiMarkdown>
                      <span className="cowork-cursor" />
                    </div>
                  </div>
                )}

                {/* Fallback only if there's no user message to anchor under (rare); normally
                    the loader renders at the top of the current turn inside the map above. */}
                {latestUserGroupIndex === -1 && turnLoader}

                <div ref={messagesEndRef} />
              </div>
              {showScrollButton && !pendingInteractive && (
                <Button
                  circular
                  className="cowork-scroll-button"
                  data-testid="cowork-scroll-button"
                  aria-label="Scroll to bottom"
                  onClick={scrollToBottom}
                  icon={<IconArrowDown aria-hidden="true" />}
                />
              )}
              {(!readOnly || canDuplicate) && <div className="cowork-wrapper">{renderInput()}</div>}
            </>
          )}
        </div>
      </div>
    </div>
  )
}

export const KodiPage = withLDConsumer()(KodiPageComponent)
